import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalStorageService } from 'src/core/services/local-storage.service';
import { LogService } from 'src/core/services/log.service';
import { UtilsService } from 'src/core/services/utils.service';
import { Log } from 'src/interfaces/log';

@Component({
    selector: 'app-log-list',
    templateUrl: './log-list.component.html',
    styleUrls: ['./log-list.component.scss'],
    standalone: false
})
export class LogListComponent implements OnInit {

  displayedColumns: string[] = [
    'entity_id',
    'entity',
    'log_type',
    'date_and_time'
  ];

  logsDataSource = new MatTableDataSource<Log>([]);
  page: number = 1;
  limit: number = 10;
  totalNumberOfLogs: number = 0;
  private resolverDataApplied = false;


  dateRangeForm = this._formBuilder.group({
    start_date: ['', Validators.required],
    end_date: ['', Validators.required]
  })

  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private _logService: LogService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _localStorageService: LocalStorageService,
    public utilService: UtilsService,
    private _formBuilder: UntypedFormBuilder,
  ) {
    const limit = this._localStorageService.get('LOGS', 'LIMIT');

    if (limit) {
      this.limit = parseInt(limit);
    }
  }

  ngOnInit(): void {
    this.applyResolvedLogs(this._route.snapshot.data['logs']);

    this
      ._route
      .data
      .subscribe(data => {
        this.applyResolvedLogs(data['logs']);
      });

    this.dateRangeForm
      .controls['start_date']
      .valueChanges
      .subscribe(value => {
        this.selectedStartDate = value;
        if (this.selectedStartDate) this.selectedStartDate.setHours(this.selectedStartDate.getHours() + 2);
      });



    this.dateRangeForm
      .controls['end_date']
      .valueChanges
      .subscribe(value => {
        this.selectedEndDate = value;
        this.page = 1;
        // only want to make a request when both dates are selected 
        if (this.selectedStartDate && this.selectedEndDate) {


          this.selectedEndDate.setHours(this.selectedEndDate.getHours() + 24);

          this.listLogs(
            this.page,
            this.limit,
            this.utilService.bestBeforeTime(this.selectedStartDate.toISOString()),
            this.utilService.bestBeforeTime(this.selectedEndDate.toISOString())
          )
        }
      })



  }

  onPageChange(event: PageEvent) {
    if (!this.resolverDataApplied) {
      return;
    }

    this.page = event.pageIndex + 1;
    this.limit = event.pageSize;

    this._localStorageService.store('LOGS', 'LIMIT', this.limit.toString());


    const start_date: string | null = this.selectedStartDate ? this.utilService.bestBeforeTime(this.selectedStartDate.toISOString()) : null;
    const end_date: string | null = this.selectedEndDate ? this.utilService.bestBeforeTime(this.selectedEndDate.toISOString()) : null;


    this.listLogs(
      this.page,
      this.limit,
      start_date,
      end_date
    );


    this.totalNumberOfLogs = this._logService.getTotalNumberOfLogs();
  }

  listLogs(
    page: number = 1,
    limit: number = 10,
    start_date: string | null = null,
    end_date: string | null = null
  ) {
    (async () => {
      const newLogs = await this._logService.getLogs(
        page,
        limit,
        '',
        start_date,
        end_date
      );
      this.logsDataSource.data = [...newLogs];
      this.totalNumberOfLogs = this._logService.getTotalNumberOfLogs();
    })();
  }

  private applyResolvedLogs(resolved: { logs?: Log[]; pageNumber?: number } | Log[] | null | undefined): void {
    if (!resolved) {
      return;
    }

    const logs = Array.isArray(resolved) ? resolved : resolved.logs ?? [];
    this.logsDataSource.data = logs;
    this.page = Array.isArray(resolved) ? this.page : (resolved.pageNumber ?? this.page);
    this.totalNumberOfLogs = this._logService.getTotalNumberOfLogs();
    this.logsDataSource.sort = this.sort;
    this.resolverDataApplied = true;
  }

  async viewLog(log: Log): Promise<void> {
    await this._logService.openLogViewDialog(log);
  }

}